import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, RotateCcw, FileText, ArrowLeft, CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";
import {
  adminApplyRefresh,
  adminMe,
  adminRefreshCandidates,
  adminSuggestRefresh,
  getAdminToken,
  setAdminToken,
} from "@/lib/api";
import { useSessionRenewal } from "@/hooks/useSessionRenewal";

export default function AdminRefresh() {
  const nav = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [cutoff, setCutoff] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [opened, setOpened] = useState({});

  useEffect(() => {
    if (!getAdminToken()) {
      nav("/admin/login", { replace: true });
      return;
    }
    adminMe()
      .then((res) => {
        if (res?.expires_at) setAdminToken(getAdminToken(), res.expires_at);
      })
      .catch(() => {
        setAdminToken("");
        nav("/admin/login", { replace: true });
      });
  }, [nav]);

  useSessionRenewal({ onExpired: () => { setAdminToken(""); nav("/admin/login", { replace: true }); } });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminRefreshCandidates();
      setCandidates(d.candidates || []);
      setCutoff(d.cutoff || "");
    } catch {
      toast.error("Could not load refresh candidates.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSuggest = async (id) => {
    setBusy((b) => ({ ...b, [id]: "suggest" }));
    try {
      const res = await adminSuggestRefresh(id);
      setOpened((o) => ({ ...o, [id]: res.suggestion }));
      toast.success(`Suggestion ready — freshness: ${res.suggestion.freshness_score}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Suggestion failed.");
    } finally {
      setBusy((b) => ({ ...b, [id]: null }));
    }
  };

  const onApply = async (id) => {
    if (!window.confirm("Replace the existing post body with the suggested revision? This cannot be undone.")) return;
    setBusy((b) => ({ ...b, [id]: "apply" }));
    try {
      await adminApplyRefresh(id);
      toast.success("Applied. The post is refreshed.");
      setOpened((o) => { const n = { ...o }; delete n[id]; return n; });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Apply failed.");
    } finally {
      setBusy((b) => ({ ...b, [id]: null }));
    }
  };

  return (
    <main data-testid="admin-refresh-page" className="bg-white min-h-screen">
      <SEO title="Content Refresh" path="/admin/refresh" noIndex />

      <section className="border-b border-[#e5e7eb] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="ash-eyebrow">Ashlan Admin</span>
            <h1 className="text-2xl md:text-3xl font-bold text-[#080808]">Content Refresh</h1>
          </div>
          <Link to="/admin/blog" className="text-sm text-[#3A7A8C] hover:underline inline-flex items-center gap-1.5">
            <ArrowLeft size={14} /> Blog console
          </Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <p className="text-sm text-[#3C4048] mb-6">
          Posts published more than <strong>90 days ago</strong> (before {cutoff?.slice(0, 10)}). Click <em>Suggest Refresh</em> and Claude will draft a tightened version while preserving the post&apos;s identity. You can then apply or discard.
        </p>

        {loading ? (
          <div className="flex items-center text-[#5A5F6A] py-12 justify-center"><Loader2 className="animate-spin mr-2" /> Loading…</div>
        ) : candidates.length === 0 ? (
          <div className="border border-dashed border-[#e5e7eb] p-12 text-center text-[#5A5F6A]" data-testid="no-candidates">
            No posts are stale yet. All Published posts are under 90 days old.
          </div>
        ) : (
          <div className="border border-[#e5e7eb] bg-white divide-y divide-[#e5e7eb]" data-testid="refresh-list">
            {candidates.map((p) => {
              const sug = opened[p.id];
              return (
                <div key={p.id} className="px-5 py-4" data-testid={`refresh-row-${p.id}`}>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex-1 min-w-[200px]">
                      <div className="font-semibold text-[#080808]">{p.title}</div>
                      <div className="text-xs text-[#5A5F6A] mt-1">
                        {p.category} · Published {p.published_at?.slice(0, 10)}
                        {p.refreshed_at && <> · Last refreshed {p.refreshed_at.slice(0, 10)}</>}
                      </div>
                    </div>
                    {!sug && (
                      <button
                        onClick={() => onSuggest(p.id)}
                        disabled={busy[p.id] === "suggest"}
                        className="ash-btn-outline inline-flex items-center gap-1.5 disabled:opacity-50"
                        data-testid={`suggest-${p.id}`}
                      >
                        {busy[p.id] === "suggest" ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                        Suggest Refresh
                      </button>
                    )}
                    <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="text-xs text-[#3A7A8C] underline inline-flex items-center gap-1">
                      <FileText size={11} /> View live
                    </a>
                  </div>

                  {sug && (
                    <div className="mt-4 bg-[#F5F5F5] border border-[#e5e7eb] p-4">
                      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <span className="text-xs uppercase tracking-[0.18em] font-semibold text-[#3A7A8C]">
                          Freshness: {sug.freshness_score}
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => onApply(p.id)}
                            disabled={busy[p.id] === "apply"}
                            className="ash-btn-primary inline-flex items-center gap-1.5 text-xs px-3 py-2 disabled:opacity-50"
                            data-testid={`apply-${p.id}`}
                          >
                            {busy[p.id] === "apply" ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                            Apply Refresh
                          </button>
                          <button
                            onClick={() => setOpened((o) => { const n = { ...o }; delete n[p.id]; return n; })}
                            className="text-xs text-[#5A5F6A] hover:text-[#080808] underline"
                          >
                            Discard
                          </button>
                        </div>
                      </div>
                      <div className="text-xs font-semibold text-[#3C4048] mb-1">Changes</div>
                      <p className="text-xs text-[#3C4048] whitespace-pre-wrap mb-3">{sug.summary_of_changes}</p>
                      <details>
                        <summary className="text-xs font-semibold text-[#3A7A8C] cursor-pointer">View revised body markdown</summary>
                        <pre className="mt-2 text-xs whitespace-pre-wrap bg-white border border-[#e5e7eb] p-3 max-h-96 overflow-y-auto leading-relaxed">{sug.revised_body_markdown}</pre>
                      </details>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}
